import { DeepLinkContext } from "../../../contexts/DeepLinkContext";
import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import API from "../../../core/api";
import ThemeExtractor from "../../../core/components/ThemeExtractor";
import { getGuestTokenCookie } from "../../../core/utils";
import OnlineInvitationRSVPForm from "../../../guest-management/forms/RSVPForm/OnlineInvitations/OnlineInvitationRSVPForm";
import RSVPSearchModal from "../../../guest-management/forms/RSVPSearchModal/RSVPSearchModal";
import ConfirmationModal from "../../../minted-components-ports/ConfirmationModal";
import { css } from "@emotion/react";
import { Button } from "@minted/fancyclothes";
import {
  Modal,
  ModalBody,
  ModalHeader,
  styleUtils,
  theme,
} from "@minted/minted-components";
import React, { useCallback, useContext, useEffect, useState } from "react";

const createRsvpStyles = (backgroundColor) => ({
  buttonWrapper: css`
    margin-left: ${theme.spacing.x4};
    margin-right: ${theme.spacing.x4};
    position: fixed;
    bottom: ${theme.spacing.x5};
    z-index: 1000;
    button {
      border: ${styleUtils.rem(1)} solid ${backgroundColor};
      width: ${styleUtils.rem(166)};
      font-size: 24px;
      box-shadow:
        0 ${styleUtils.rem(4)} ${styleUtils.rem(8)} 0 rgba(0, 0, 0, 0.2),
        0 ${styleUtils.rem(6)} ${styleUtils.rem(20)} 0 rgba(0, 0, 0, 0.19);
    }
    ${theme.media.greaterThan(styleUtils.rem(768))`
      button {
        width: ${styleUtils.rem(200)};
      }
    `};
  `,
  modalHeaderText: css`
    color: ${theme.colors.black};
  `,
});

const OnlineInvitationRSVPButton = ({ event, site }) => {
  const { isPreview } = useIsPreviewOrDemo();
  const [isRSVPFormOpen, setIsRSVPFormOpen] = useState(false);
  const [isRSVPSearchFormOpen, setIsRSVPSearchFormOpen] = useState(false);
  const [rsvpData, setRSVPData] = useState(null);
  const [existingRsvpAcknowledged, setExistingRsvpAcknowledged] =
    useState(true);

  const { addressbookContactId } = useContext(DeepLinkContext);

  // Load RSVP form data if deep link is provided
  useEffect(() => {
    if (addressbookContactId && !isPreview) {
      const guestToken = getGuestTokenCookie();
      const headers = {
        Authorization: `Guest ${guestToken}`,
      };

      API.get(
        "guest-management/rsvp-form-data",
        {
          headers,
        },
        {
          addressbook_contact_id: addressbookContactId,
        },
      ).then((responseData) => {
        if (responseData.contact) {
          // For OI, event title comes from site details
          responseData.events[0].title = site.siteDetails.eventHeading;
          setRSVPData(responseData);
          setExistingRsvpAcknowledged(!responseData.hasExistingRsvps);
        }
      });
    }
  }, [
    addressbookContactId,
    isPreview,
    setRSVPData,
    setIsRSVPFormOpen,
    site.siteDetails.eventHeading,
  ]);

  const setRsvpModalData = useCallback((rsvpData) => {
    setRSVPData(rsvpData);
    setExistingRsvpAcknowledged(!rsvpData.hasExistingRsvps);
    setIsRSVPSearchFormOpen(false);
    setIsRSVPFormOpen(true);
  }, []);

  const exitForm = useCallback(() => {
    setIsRSVPFormOpen(false);
    setRSVPData(null);
    setExistingRsvpAcknowledged(true);
  }, []);

  const goBackToSearch = useCallback(() => {
    setIsRSVPFormOpen(false);
    setRSVPData(null);
    setExistingRsvpAcknowledged(true);
    setIsRSVPSearchFormOpen(true);
  }, []);

  const onRSVPButtonClick = useCallback(() => {
    if (isPreview) {
      return;
    }
    if (rsvpData) {
      setIsRSVPFormOpen(true);
    } else {
      setIsRSVPSearchFormOpen(true);
    }
  }, [isPreview, rsvpData]);

  return (
    <ThemeExtractor rules={["backgroundColor"]}>
      {({ backgroundColor }) => {
        const rsvpStyles = createRsvpStyles(backgroundColor);

        return (
          <>
            <div data-cy="floatingRSVP" css={rsvpStyles.buttonWrapper}>
              <Button
                floating
                onClick={onRSVPButtonClick}
                text="RSVP"
                xPosition="left"
                yPosition="bottom"
                disabled={isPreview}
              />
            </div>
            {isRSVPSearchFormOpen && (
              <RSVPSearchModal
                closeModal={() => setIsRSVPSearchFormOpen(false)}
                initialSearchTerm=""
                openInvitation={event.openInvitation}
                setRsvpData={setRsvpModalData}
                site={site}
              />
            )}

            {isRSVPFormOpen && rsvpData && !existingRsvpAcknowledged && (
              <ConfirmationModal
                onCloseClick={goBackToSearch}
                onSubmit={() => setExistingRsvpAcknowledged(true)}
                open
                showCancel
                submitButtonText="Open RSVP Form"
                alertPrimaryText="An RSVP was already submitted."
              >
                If you need to change your answers, contact the host or submit
                this form again.
              </ConfirmationModal>
            )}

            {isRSVPFormOpen && rsvpData && existingRsvpAcknowledged && (
              <Modal
                mobileFullscreen
                onCloseClick={exitForm}
                open
                size="medium"
              >
                <ModalHeader>
                  <div css={rsvpStyles.modalHeaderText}>RSVP</div>
                </ModalHeader>
                <ModalBody>
                  <OnlineInvitationRSVPForm
                    contact={rsvpData.contact}
                    event={rsvpData.events[0]}
                    guest={rsvpData.guests[0]}
                    onCancelRsvp={goBackToSearch}
                    siteId={site.id}
                  />
                </ModalBody>
              </Modal>
            )}
          </>
        );
      }}
    </ThemeExtractor>
  );
};

export default OnlineInvitationRSVPButton;
